import { useState } from "react";
import { CheckCircle2, Mail, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

/**
 * Stand-ins for the two verification surfaces that cannot run without a
 * session token, shared by the applicant and voucher demos.
 *
 * `EmailCodePanel` calls /verify/email/send-code, and `PlaidIDVButton` mints a
 * Plaid link token. Both are gated on `isLive` at every call site, so in demo
 * they rendered NOTHING — an email dialog with no body, and an ID step with no
 * button. These walk the same states against fixed values, and say on screen
 * that they are simulated: an audience should never be unsure which parts of a
 * demo were real.
 *
 * Not wired to Plaid sandbox. That needs a real session neither demo has.
 */

/* ============================================================================
 * Demo-mode OTP panel — the "Quick code" row on step 1.
 *
 * `EmailCodePanel` is gated on a session token (it calls
 * /verify/email/send-code and /confirm-code), so in demo the Quick code row
 * used to expand onto nothing at all: a toggle that changed its own label and
 * produced no panel. This walks the same three states — enter address, enter
 * code, verified — against a fixed code, so the shape of the step is
 * demonstrable without a backend.
 *
 * The code is shown on screen rather than hidden. A demo that asks someone to
 * guess is a demo that stalls, and inventing a "check your email" that never
 * arrives would be a dead end of exactly the kind soul.md rules out.
 *
 * Wrong entries say so plainly and leave what was typed in the field — the
 * live panel's rule, and for the same reason: clearing an input on a failed
 * attempt is how the real one once deadlocked (see CLAUDE.md, J5).
 * ========================================================================== */
const DEMO_OTP_CODE = "123456";

/**
 * Which demo lane the viewer was last on, so a switch between lanes can start
 * the new one clean. sessionStorage rather than a ref: leaving for the demo hub
 * unmounts this component, and the marker has to outlive that.
 */
const DEMO_LANE_KEY = "dv_demo_lane";

export function DemoEmailCodePanel({
  onVerified,
  presentation = "demo",
}: {
  onVerified: (email: string) => void;
  presentation?: "demo" | "implementation";
}) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailLooksUsable = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  if (!sent) {
    return (
      <div className="space-y-2">
        <Input
          type="email"
          inputMode="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button
          variant="outline"
          className="w-full"
          disabled={!emailLooksUsable}
          onClick={() => { setSent(true); setError(null); }}
        >
          <Mail className="w-4 h-4 mr-2" />
          Send verification code
        </Button>
        <p className="text-[11px] text-muted-foreground italic text-center">
          {presentation === "implementation"
            ? "Implementation preview — no email is sent from this review route."
            : "Demo — no email is sent."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Enter the 6-digit code for{" "}
        <span className="font-mono text-foreground">{email.trim()}</span>
        {`. For this ${presentation === "implementation" ? "review" : "demo"} the code is `}<span className="font-mono font-semibold text-foreground">{DEMO_OTP_CODE}</span>.
      </p>
      <Input
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        placeholder="123456"
        value={code}
        onChange={(e) => { setCode(e.target.value); setError(null); }}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button
          className="flex-1"
          disabled={code.length === 0}
          onClick={() => {
            if (code.trim() === DEMO_OTP_CODE) {
              onVerified(email.trim());
              return;
            }
            setError("That code doesn't match. Check it and try again.");
          }}
        >
          Verify
        </Button>
        <Button variant="ghost" onClick={() => { setSent(false); setCode(""); setError(null); }}>
          Use a different email
        </Button>
      </div>
    </div>
  );
}


/* ============================================================================
 * Demo-mode ID check.
 *
 * Copy rules follow the real card: never the word "optional" on an IDV screen,
 * and none of "fail" / "risk" / "fraud" / "pass". A declined or failed ID check
 * is a workflow fact, never an accusation.
 * ========================================================================== */
export function DemoIDVPanel({
  verified,
  onVerify,
  footnote,
  presentation = "demo",
}: {
  verified: boolean;
  onVerify: () => void;
  /** Rendered under the card. The two flows say different things about who sees the result. */
  footnote?: React.ReactNode;
  presentation?: "demo" | "implementation";
}) {
  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-5 space-y-4">
          {verified ? (
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 mt-0.5 text-trust-green shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-semibold">ID verified</p>
                <p className="text-sm text-muted-foreground">
                  Your identity document and selfie matched. Nothing else to do here.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-semibold">Verify your ID</p>
                  <p className="text-sm text-muted-foreground">
                    Scan a government ID and take a quick selfie. About a minute.
                  </p>
                </div>
              </div>
              <Button onClick={onVerify} className="w-full sm:w-auto">
                Start ID check
              </Button>
            </>
          )}
          <p className="text-xs text-muted-foreground border-t pt-3">
            {presentation === "implementation"
              ? "Implementation preview — no ID is collected and Plaid is not called from this review route."
              : "Demo — this simulates the result. No ID is collected and Plaid is not called."}
          </p>
        </CardContent>
      </Card>
      {footnote}
    </div>
  );
}
